if (!window.Eurus.loadedScript.has('variant-select.js')) {
  window.Eurus.loadedScript.add('variant-select.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xVariantSelect', (
        element,
        sectionId,
        isProductPage,
        unavailableText,
        productUrl,
        optionNum,
        cloneSectionId = element.closest('.data-id-section-card') ? element.closest('.data-id-section-card').id : ''
      ) => ({
        loadingEls: '',
        currentVariant: {},
        cachedResults: [],
        prefetchInFlight: {},
        handleSectionId: sectionId,
        mediaGallerySource: [],
        optionConnects: [],
        mediaOption: "",
        handleSticky: '',
        initfirstMedia: false,
        // Section setting "Require an explicit variant selection". The shopper starts
        // with nothing chosen; the form still carries a resolved variant id, so the
        // gallery and price behave normally while add-to-cart stays gated until every
        // option has actually been touched.
        requireExplicitSelection: element.dataset.requireExplicitSelection === 'true',
        // Option groups (by data-option-name) the shopper has picked from. Kept on the
        // component rather than read from the DOM because the server re-render marks
        // the whole resolved combination as selected, which would silently fill in
        // options nobody chose.
        touchedOptions: new Set(),
        altGroupMap: null,
        init() {
          Array.from(element.querySelectorAll('input')).forEach(input => {
            input.addEventListener('change', (e) => {
              this._markOptionTouched(e.target);
              setTimeout(() => {
                this.updateVariantSelector(e.target.id, e.target.dataset.productUrl);
              }, 0);
            })
          });
          Array.from(element.querySelectorAll('select')).forEach(select => {
            select.addEventListener('change', (e) => {
              this._markOptionTouched(e.target);
              setTimeout(() => {
                Array.from(e.target.options).forEach(option => { option.removeAttribute('selected') });
                e.target.options[e.target.selectedIndex].setAttribute('selected', 'selected');
                this.updateVariantSelector(e.target.id, e.target.options[e.target.selectedIndex].dataset.productUrl);
              }, 0)
            })
          });

          // Switching a variant is gated entirely on the section-HTML fetch in
          // _renderProductInfo — when cachedResults already holds the response the
          // swap is instant, when it doesn't the whole UI waits on a server render.
          // So warm that cache before the click lands. Delegated on `element`
          // (which is never replaced, only its innerHTML) so it survives every
          // picker swap without rebinding.
          ['pointerover', 'focusin'].forEach((evt) => {
            element.addEventListener(evt, (e) => {
              const label = e.target.closest && e.target.closest('label[for]');
              if (!label) return;
              const input = document.getElementById(label.htmlFor);
              if (input && input.type === 'radio' && !input.checked && element.contains(input)) {
                this.prefetchVariant(input);
              }
            }, { passive: true });
          });

          // Touch has no hover, so pointerover fires only a few ms before the tap.
          // Warm every one-click-away combination on idle instead — after load, one
          // at a time, so it never competes with the page's own resources.
          this._scheduleWarmAdjacentVariants();

          // Nothing is touched yet, so publish the "incomplete" state before the
          // shopper can reach the add-to-cart button.
          this._syncSelectionState();
        },
        _onIdle(fn) {
          return ('requestIdleCallback' in window)
            ? requestIdleCallback(fn, { timeout: 4000 })
            : setTimeout(fn, 300);
        },
        _warmAdjacentVariants() {
          // Warm every combination that is one click away from the current
          // selection. Re-runnable: after a swap the adjacent set changes for
          // multi-option products (for a single-option product the links are
          // selection-independent, so the repeat is a no-op against cachedResults).
          const conn = navigator.connection;
          if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;

          const inputs = Array.from(element.querySelectorAll('input[type="radio"][data-option-value-id]'))
            .filter(input => !input.checked)
            .slice(0, 10);

          // Sequential: the next prefetch is queued only once the previous
          // response has landed, so we never open 10 section requests at once.
          const next = () => {
            const input = inputs.shift();
            if (!input) return;
            this._onIdle(() => { Promise.resolve(this.prefetchVariant(input)).finally(next); });
          };
          next();
        },
        _scheduleWarmAdjacentVariants() {
          if (!isProductPage) return;
          const start = () => this._onIdle(() => this._warmAdjacentVariants());
          if (document.readyState === 'complete') start();
          else window.addEventListener('load', start, { once: true });
        },
        prefetchVariant(input) {
          // Mirrors the link _renderProductInfo will build for this input, so the
          // click hits cachedResults instead of the network. Kept deliberately in
          // sync with updateVariantSelector/_renderProductInfo below.
          if (this.handleSticky !== '') return;

          const targetUrl = input.dataset.productUrl || element.dataset.url;
          const updateFullpage = isProductPage && element.dataset.url !== targetUrl;
          const optionValues = this._optionValuesWith(input);
          if (optionValues.length < optionNum) return;

          const params = `option_values=${optionValues.join(',')}`;
          const link = updateFullpage ? `${targetUrl}?${params}` : `${targetUrl}?section_id=${this.handleSectionId}&${params}`;

          this._warmVariantMedia(input);

          if (this.cachedResults[link]) return;
          if (this.prefetchInFlight[link]) return this.prefetchInFlight[link];

          // No AbortController: prefetches are deliberately not registered in
          // xHelper's request map, so a real click never cancels them (and they
          // never cancel a real click's request).
          const request = fetch(link)
            .then(response => response.text())
            .then(responseText => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              this.cachedResults[link] = html;
              return html;
            })
            .catch(() => null)
            .finally(() => { delete this.prefetchInFlight[link]; });

          this.prefetchInFlight[link] = request;
          return request;
        },
        _optionValuesWith(input) {
          // Same ids _getSelectedOptionValues would return, but with `input`
          // substituted for whatever is currently selected in its own option group.
          const ids = [];
          element.querySelectorAll('[data-option-name]').forEach((group) => {
            const el = group.contains(input) ? input : group.querySelector('select option[selected], input:checked');
            if (el && el.dataset.optionValueId !== undefined) ids.push(el.dataset.optionValueId);
          });
          return ids;
        },
        _warmVariantMedia(input) {
          // Every variant's gallery image is already in the DOM; the ones past the
          // first slide are loading="lazy" and so haven't been fetched. Flipping
          // the real element to eager starts the download using its own
          // srcset/sizes, which is the only way to guarantee the browser picks the
          // same candidate it will actually render.
          const el = this._getVariantDataElement(input.id);
          if (!el) return;
          let mediaId;
          try {
            mediaId = JSON.parse(el.textContent)?.featured_media?.id;
          } catch (e) {
            return;
          }
          if (!mediaId) return;
          document
            .querySelectorAll(`#shopify-section-${sectionId} [data-media-id="${sectionId}-${mediaId}"] img[loading="lazy"]`)
            .forEach(img => { img.loading = 'eager'; });
        },
        _dispatchVariantSelected(html) {
          document.dispatchEvent(new CustomEvent(`eurus:product-page-variant-select:updated:${sectionId}`, {
            detail: { html: html }
          }));
        },
        _dispatchUpdateVariant(html="") {
          document.dispatchEvent(new CustomEvent(`eurus:product-card-variant-select:updated:${sectionId}`, {
            detail: {
              currentVariant: this.currentVariant,
              html: html
            }
          }));
        },
        _renderDestination(html, selector) {
          const destination = document.getElementById(selector + sectionId);
          const source = html.getElementById(selector + sectionId);
          if (source && destination) destination.innerHTML = source.innerHTML;
        },
        _goToSelectedVariantMedia() {
          let splideEl = document.getElementById("x-product-" + sectionId);
          let slideVariant = ""
          let index = ""
          let activeEL = ""
          if (this.currentVariant !== undefined && this.currentVariant?.featured_media !== undefined) {
            slideVariant = document.getElementsByClassName(this.currentVariant.featured_media.id + '-' + sectionId);
            index = parseInt(slideVariant[0]?.getAttribute('index'));
            activeEL = document.getElementById('postion-image-' + sectionId + '-' + this.currentVariant.featured_media.id);
          } else {
            slideVariant = splideEl.querySelector(".featured-image");
            index = parseInt(slideVariant?.getAttribute('index'));
            activeEL = document.querySelector(`#stacked-${sectionId} .featured-image`);
          }
          
          if (splideEl) {
            if (splideEl.splide && slideVariant) {
              splideEl.splide.go(index)
            } else {
              document.addEventListener(`eurus:media-gallery-ready:${sectionId}`, () => {
                if (splideEl.splide)
                  splideEl.splide.go(index);
              });
            }
          }

          return activeEL;
        },
        _updateMedia(html) {
          let mediaWithVariantSelected = document.getElementById("product-media-" + sectionId) && document.getElementById("product-media-" + sectionId).dataset.mediaWithVariantSelected;
          
          if (!mediaWithVariantSelected) {
            let activeEL = this._goToSelectedVariantMedia();
            if (!activeEL) return;

            if (html && !mediaWithVariantSelected) {
              let mediaGalleryDestination = html.getElementById(`stacked-${ sectionId }`);
              let mediaGallerySource = document.getElementById(`stacked-${ sectionId }`);

              if (mediaGallerySource && mediaGalleryDestination) {
                let firstChildSource = mediaGallerySource.querySelectorAll('div[data-media-id]')[0];
                let firstChildDestination = mediaGalleryDestination.querySelectorAll('div[data-media-id]')[0];
                if (firstChildDestination.dataset.mediaId != firstChildSource.dataset.mediaId && firstChildSource.dataset.index != 1) {
                  let sourceIndex = parseInt(firstChildSource.dataset.index);  
                  let positionOld = mediaGallerySource.querySelector(`div[data-media-id]:nth-of-type(${sourceIndex + 1})`);
                  mediaGallerySource.insertBefore(firstChildSource, positionOld);
                }

                mediaGallerySource.prepend(activeEL);
              }
            }
          }
          if (mediaWithVariantSelected) {
            this.updateMultiMediaWithVariant();
          }
        },
        _validateOption() {
          const mediaWithOption = document.querySelector(`#shopify-section-${sectionId} [data-media-option]`);
          if (mediaWithOption)
            this.mediaOption = mediaWithOption.dataset.mediaOption.split('_');
        },
        updateMultiMediaWithVariant() {
          // Alt-code grouping takes over completely when the section opts in — the
          // option-name matching below cannot resolve a product whose options share
          // one value list. Falls through to the stock path if the map is missing or
          // the variant's group has no media, so a mistagged image degrades rather
          // than emptying the gallery.
          if (this.updateMediaByAltGroup()) return;
          this._validateOption();
          if (!this.currentVariant) {
            if (this.initfirstMedia) {
              let productMedia = document.querySelectorAll( `#ProductModal-${ sectionId } [data-media-option], #shopify-section-${ sectionId } [data-media-option]`);
              Array.from(productMedia).reverse().forEach(function(newMedia, position) {
                newMedia.classList.add('media_active');
                if (newMedia.classList.contains('media-slide')) {
                  newMedia.classList.add('splide__slide');
                }
              });
            }
            return;
          }
          const variantInputs = this.mediaOption.map(option =>
            document.querySelector(`#shopify-section-${sectionId} [data-option-name="${option}"]`)
          ).filter(el => el !== null);
          if (variantInputs.length === 0) {
            let variantMedias = ""
            if (!this.currentVariant?.featured_media?.id) {
              variantMedias = document.querySelectorAll(`#ProductModal-${ sectionId } [data-media-option].featured-image, #shopify-section-${ sectionId } [data-media-option].featured-image`); 
            } else {
              variantMedias = document.querySelectorAll(`#ProductModal-${ sectionId } [data-media-option="${sectionId}-${this.currentVariant?.featured_media?.id}"], #shopify-section-${ sectionId } [data-media-option="${sectionId}-${this.currentVariant?.featured_media?.id}"]`);
            }
            let mediaActive = document.querySelectorAll(`#ProductModal-${ sectionId } [data-media-option=""], #shopify-section-${ sectionId } [data-media-option=""]`);
            let productMedias = document.querySelectorAll( `#ProductModal-${ sectionId } [data-media-option], #shopify-section-${ sectionId } [data-media-option]`);
            const newMedias = Array.prototype.concat.call( ...mediaActive, ...variantMedias)
            this._setActiveMedia(productMedias, newMedias, variantMedias);

            let splideEl = document.getElementById(`x-product-${ sectionId }`);
            if (splideEl.splide) {
              splideEl.splide.refresh();
              splideEl.splide.go(0);
            }
            let splideZoomEl = document.getElementById(`media-gallery-${ sectionId }`);
            if (splideZoomEl.splide) {
              splideZoomEl.splide.refresh();
            }
          } else {
            let optionConnects = [];
            variantInputs.forEach((variantInput) => {
              const variantOptionIndex = variantInput && variantInput.dataset.optionIndex;
              const optionValue = this._handleText(this.currentVariant?.options[variantOptionIndex]);
              if (this.mediaOption.includes(variantInput.dataset.optionName)) {
                optionConnects.push(variantInput.dataset.optionName + '-' + optionValue);
              }
              this.optionIndex = variantOptionIndex;
            });
            const mediaActive = document.querySelectorAll(`#ProductModal-${ sectionId } [data-media-type=""], #shopify-section-${ sectionId } [data-media-type=""]`);
            
            let variantMedias = [];
            let allVariantMedias = document.querySelectorAll(`#ProductModal-${ sectionId } [data-media-type]:not([data-media-type=""]), #shopify-section-${ sectionId } [data-media-type]:not([data-media-type=""])`);
            allVariantMedias.forEach((variantMedia) => {
              let data = variantMedia.getAttribute('data-media-type');
              let dataSet = new Set(data.split('_'));
              if (optionConnects.filter(option => dataSet.has(option)).length === dataSet.size) variantMedias.push(variantMedia);
            });

            let showFeatured = false;
            if (!variantMedias.length) {
              if (!this.currentVariant?.featured_media?.id) {
                variantMedias = document.querySelectorAll(`#ProductModal-${ sectionId } [data-media-option].featured-image, #shopify-section-${ sectionId } [data-media-option].featured-image`); 
                showFeatured = true;
              } else {
                variantMedias = document.querySelectorAll(`#ProductModal-${ sectionId } [data-media-id="${sectionId}-${this.currentVariant?.featured_media?.id}"], #shopify-section-${ sectionId } [data-media-id="${sectionId}-${this.currentVariant?.featured_media?.id}"]`);
              }
            }
            if (!variantMedias.length) {
              document.querySelectorAll( `#ProductModal-${ sectionId } [data-media-type], #shopify-section-${ sectionId } [data-media-type]`).forEach(function(media){
                media.classList.add('media_active');
                media.classList.add('splide__slide');
              });
              let splideEl = document.getElementById(`x-product-${ sectionId }`);
              if (splideEl.splide) {
                splideEl.splide.refresh();
                splideEl.splide.go(0);
              }
              let splideZoomEl = document.getElementById(`media-gallery-${ sectionId }`);
              if (splideZoomEl.splide) {
                splideZoomEl.splide.refresh();
              }
              return;
            }
            
            const newMedias = Array.prototype.concat.call(...variantMedias , ...mediaActive);
            let productMedias = document.querySelectorAll( `#shopify-section-${ sectionId } [data-media-type], #ProductModal-${ sectionId } [data-media-type]`);
            
            this._setActiveMedia(productMedias, newMedias);
            
            if (this.optionConnect != optionConnects) {
              this.optionConnect = optionConnects;
            }
            
            let splideEl = document.getElementById(`x-product-${ sectionId }`);
            if (splideEl.splide) {
              splideEl.splide.refresh();
              splideEl.splide.go(0);
            }
            let splideZoomEl = document.getElementById(`media-gallery-${ sectionId }`);
            if(splideZoomEl && splideZoomEl.splide){
              splideZoomEl.splide.refresh();
            }
            
            if (showFeatured) {
              this._goToFirstSlide();
            }  
          }
        },
        // --- Explicit-selection tracking ------------------------------------
        _optionNameFor(input) {
          const group = input && input.closest('[data-option-name]');
          return group ? group.dataset.optionName : null;
        },
        _markOptionTouched(input) {
          const name = this._optionNameFor(input);
          if (name) this.touchedOptions.add(name);
          this._syncSelectionState();
        },
        // Publishes completeness onto the element so addToCart in product-cart.js can
        // read it straight off the DOM. Deliberately not exposed as a cross-component
        // Alpine call - Alpine.$data lands in 3.10 and this theme pins 3.10.5, which
        // is too close to the line to depend on.
        _syncSelectionState() {
          if (!this.requireExplicitSelection) return;
          const complete = this.allOptionsSelected();
          element.dataset.allOptionsSelected = complete ? 'true' : 'false';
          // Clear the add-to-cart error as soon as the last option is filled in,
          // rather than leaving it up until the next submit attempt.
          if (complete) {
            const productInfo = element.closest('.product-info');
            const errorEl = productInfo && productInfo.querySelector('.variant-selection-error');
            if (errorEl) errorEl.classList.add('hidden');
          }
        },
        // True once the shopper has picked a value in every option group. Read by
        // addToCart in product-cart.js to decide whether to block the submit.
        allOptionsSelected() {
          if (!this.requireExplicitSelection) return true;
          const groups = element.querySelectorAll('[data-option-name]');
          if (!groups.length) return true;
          return Array.from(groups).every(group => this.touchedOptions.has(group.dataset.optionName));
        },
        // The server re-render resolves a whole variant, so it comes back with every
        // option marked selected. Strip the selection back off any group the shopper
        // has not actually touched, so picking Flavor 1 does not silently fill in
        // Flavor 2 as well.
        _clearUntouchedOptions() {
          if (!this.requireExplicitSelection) return;
          element.querySelectorAll('[data-option-name]').forEach(group => {
            if (this.touchedOptions.has(group.dataset.optionName)) return;
            group.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = false; });
            group.querySelectorAll('select').forEach(select => {
              Array.from(select.options).forEach(option => option.removeAttribute('selected'));
              const blank = select.querySelector('option[id$="-blank"]');
              if (blank) {
                blank.setAttribute('selected', 'selected');
                select.selectedIndex = blank.index;
              }
            });
          });
        },
        // --- Alt-code media grouping ----------------------------------------
        _getAltGroupMap() {
          if (this.altGroupMap !== null) return this.altGroupMap;
          const el = document.getElementById(`media-alt-groups-${sectionId}`);
          if (!el) {
            this.altGroupMap = false;
            return this.altGroupMap;
          }
          try {
            this.altGroupMap = JSON.parse(el.textContent);
          } catch (err) {
            this.altGroupMap = false;
          }
          return this.altGroupMap;
        },
        // Shows every image sharing the selected variant's alt-tag group code. The
        // variant supplies the code through its own attached image; the rest of the
        // group rides along on the matching alt, which is the only way to express
        // more than one image per combination (a variant has just one featured image).
        updateMediaByAltGroup() {
          const map = this._getAltGroupMap();
          if (!map || !this.currentVariant) return false;
          const group = map.variants[this.currentVariant.id];
          if (!group) return false;

          const groupMediaIds = new Set(
            Object.keys(map.media).filter(mediaId => map.media[mediaId] === group)
          );
          if (!groupMediaIds.size) return false;

          // Scoped to elements carrying BOTH attributes: data-media-id alone also
          // matches nested video controls inside a slide, which must not have their
          // classes toggled. data-media-type is present on exactly the gallery
          // containers the stock path operates on, since this mode requires
          // show_media_with_tag_alt.
          const productMedias = document.querySelectorAll(
            `#shopify-section-${sectionId} [data-media-type][data-media-id], #ProductModal-${sectionId} [data-media-type][data-media-id]`
          );
          // data-media-id is emitted in several shapes across the gallery's markup
          // branches ("<section>-<media>", "zoom-<media>", "zoom-<section>-<media>"),
          // so match on the trailing media id rather than the whole string.
          const newMedias = Array.from(productMedias).filter(el => {
            const raw = el.getAttribute('data-media-id') || '';
            return groupMediaIds.has(raw.split('-').pop());
          });
          if (!newMedias.length) return false;

          this._setActiveMedia(productMedias, newMedias);
          this._refreshSplide();
          return true;
        },
        _refreshSplide() {
          const splideEl = document.getElementById(`x-product-${sectionId}`);
          if (splideEl && splideEl.splide) {
            splideEl.splide.refresh();
            splideEl.splide.go(0);
          }
          const splideZoomEl = document.getElementById(`media-gallery-${sectionId}`);
          if (splideZoomEl && splideZoomEl.splide) {
            splideZoomEl.splide.refresh();
          }
        },
        _setActiveMedia(productMedias, newMedias, activeMedia) {
          productMedias.forEach(function(media){
            media.classList.remove('media_active');
            media.classList.remove('splide__slide');
            media.classList.remove('x-thumbnail');
          });
          Array.from(newMedias).reverse().forEach(function(newMedia, position) {
            newMedia.classList.add('media_active');
            if (newMedia.classList.contains('media-thumbnail')) {
              newMedia.classList.add('x-thumbnail');
            }
            if (newMedia.classList.contains('media-slide')) {
              newMedia.classList.add('splide__slide');
            }
            let parent = newMedia.parentElement;
            if (activeMedia) {
              if (parent.firstChild != newMedia && Array.from(activeMedia).includes(newMedia)) {
                parent.prepend(newMedia);
              }
            } else {
              if (parent.firstChild != newMedia) {
                parent.prepend(newMedia);
              }
            }
          });

          if (activeMedia) {
            let parent = activeMedia.parentElement;
            parent && parent.prepend(activeMedia);
          }
        },
        _handleText(someString) {
          if (someString) {
            return someString.toString().replace('ı', 'i').replace('ß', 'ss').normalize('NFC').replace('-', ' ').toLowerCase().trim().replace(/[^\p{L}\p{N}\s-]/gu, '').replace(/\s+/g, "-");
          }
        },
        _goToFirstSlide() {
          if (this.currentVariant && !this.currentVariant.featured_image) {
            let splideEl = document.getElementById("x-product-" + sectionId);
            if (splideEl) {
              if (splideEl.splide && this.currentVariant && this.currentVariant.featured_image != null) {
                splideEl.splide.go(0);
              }
            }

            let activeEL = document.querySelector(`#stacked-${sectionId} .featured-image`);
            let stackedEL = document.getElementById('stacked-' + sectionId);
            if(stackedEL && activeEL) stackedEL.prepend(activeEL);
          }
        },
        _updateProductForms() {
          const productForms = document.querySelectorAll(`#product-form-${sectionId}, #product-form-installment-${sectionId}, #product-form-sticky-${sectionId}`);
          productForms.forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            if (input) {
              input.value = this.currentVariant?.id;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        },
        _renderPriceProduct(html) {
          const destination = document.getElementById('price-' + sectionId);
          let source = html.getElementById('price-' + sectionId);
          if (!this.currentVariant) {
            destination?.classList.add('hidden');
            return;
          } else {
            destination?.classList.remove('hidden');
          }
          if(!source) {
            source = html.querySelector('.price');
            if (source && destination) destination.innerHTML = source.outerHTML;
          } else {
            if (source && destination) destination.innerHTML = source.innerHTML;
          }
        },
        _renderBuyButtons(html) {
          const productForms = document.querySelectorAll(`#product-form-${sectionId}, #product-form-installment-${sectionId}, #product-form-sticky-${sectionId}`);
          
          productForms.forEach((productForm) => {
            const atcSource = html.querySelector(`#${productForm.getAttribute("id")} .add_to_cart_button`);
            const atcDestination = productForm.querySelector('.add_to_cart_button');
            if (!atcDestination) return;

            if (atcSource && atcDestination) atcDestination.innerHTML = atcSource.innerHTML;
    
            if (this.currentVariant?.available) {
              /// Enable add to cart button
              atcDestination.dataset.available = "true";
              if (html.getElementById('form-gift-card-' + sectionId)) {
                if (document.getElementById('Recipient-checkbox-' + sectionId).checked && document.getElementById('recipient-form-' + sectionId).dataset.disabled == "true") {
                  atcDestination.setAttribute('disabled', 'disabled') 
                } else {
                  atcDestination.removeAttribute('disabled');
                }
              } else {
                atcDestination.removeAttribute('disabled');
              }
            } else {
              atcDestination.dataset.available = "false";
              atcDestination.setAttribute('disabled', 'disabled');
            }

            const cloneProductForms = document.querySelectorAll(`#product-form-${cloneSectionId}, #product-form-installment-${cloneSectionId}, #product-form-sticky-${cloneSectionId}`);
            cloneProductForms.forEach((cloneProductForm) => {
              if (cloneProductForm.getAttribute("id").includes(productForm.getAttribute("id"))){
                const atcCloneDestination = cloneProductForm.querySelector('.add_to_cart_button');
                if (!atcCloneDestination) return;

                if (atcSource && atcCloneDestination) atcCloneDestination.innerHTML = atcSource.innerHTML;
        
                if (this.currentVariant?.available) {
                  /// Enable add to cart button
                  atcCloneDestination.dataset.available = "true";
                  if (html.getElementById('form-gift-card-' + sectionId)) {
                    if (document.getElementById('Recipient-checkbox-' + sectionId).checked && document.getElementById('recipient-form-' + sectionId).dataset.disabled == "true") {
                      atcCloneDestination.setAttribute('disabled', 'disabled') 
                    } else {
                      atcCloneDestination.removeAttribute('disabled');
                    }
                  } else {
                    atcCloneDestination.removeAttribute('disabled');
                  }
                } else {
                  atcCloneDestination.dataset.available = "false";
                  atcCloneDestination.setAttribute('disabled', 'disabled');
                }
              }
            })

          });
          const paymentButtonDestination = document.getElementById('x-payment-button-' + sectionId);
          const paymentButtonSource = html.getElementById('x-payment-button-' + sectionId);
          if (paymentButtonSource && paymentButtonDestination) {
            if (paymentButtonSource.classList.contains('hidden')) {
              paymentButtonDestination.classList.add('hidden');
            } else {
              paymentButtonDestination.classList.remove('hidden');
            }
          }
        },
        _setMessagePreOrder(html) {
          const msg = document.querySelector(`.pre-order-${sectionId}`);
          if (!msg) return;
          msg.classList.add('hidden');
          const msg_pre = html.getElementById(`pre-order-${sectionId}`);
          if (msg_pre) {
            msg.classList.remove('hidden');
            msg.innerHTML = msg_pre.innerHTML;
          }
        },
        _setEstimateDelivery(html) {
          const est = document.getElementById(`x-estimate-delivery-container-${sectionId}`);
          if (!est) return;
          const est_res = html.getElementById(`x-estimate-delivery-container-${sectionId}`);
          if (est_res.classList.contains('disable-estimate')) {
            est.classList.add('hidden');
          } else {
            est.classList.remove('hidden');
            est.innerHTML = est_res.innerHTML;
          }

          const estimateDeliveryCart = document.querySelectorAll(`.cart-edt-${sectionId}`);
          const estimateDeliveryCartUpdate = html.querySelectorAll(`.cart-edt-${sectionId}`);
          if (estimateDeliveryCart.length > 0 && estimateDeliveryCartUpdate.length > 0) {
            estimateDeliveryCart.forEach((item, index) => {
              if(estimateDeliveryCartUpdate[index] != undefined && estimateDeliveryCartUpdate[index].innerHTML != undefined ){
                item.innerHTML = estimateDeliveryCartUpdate[index].innerHTML;
              }
            })
          }
        },
        _setMetafieldInfo(html, query) {
          const content_arr = document.querySelectorAll(`${query}-${sectionId}`);
          const content_res_arr = html.querySelectorAll(`${query}-${sectionId}`);
          if (content_arr.length > 0 && content_res_arr.length > 0) {
            content_arr.forEach((toc, index) => {
              toc.innerHTML = content_res_arr[index].innerHTML;
            })
          }
        },
        _setBackInStockAlert(html) {
          const destination = document.getElementById(`back_in_stock_alert-${sectionId}`);
          const source = html.getElementById(`back_in_stock_alert-${sectionId}`);
          if (source && destination) destination.innerHTML = source.innerHTML;
        },
        _setPickupPreOrder(html) {
          const pickup = document.getElementById(`pickup-pre-order-${sectionId}`);
          if (!pickup) return;
          const pickup_res = html.getElementById(`pickup-pre-order-${sectionId}`);
          if (pickup_res.classList.contains('disable-pickup')) {
            pickup.classList.add('hidden');
          } else {
            pickup.classList.remove('hidden');
          }
        },
        _setUnavailable() {
          const selectors = ['price-sticky-', 'block-inventory-', 'x-badges-', 'pickup-', 'sku-', 'back_in_stock_alert-'];
          for (let selector of selectors) {
            const element = document.getElementById(selector + sectionId);
            if (element) element.classList.add('hidden');
          }
          
          const msg_pre = document.querySelector(`.pre-order-${sectionId}`);
          if (msg_pre) msg_pre.classList.add('hidden');
          const quantity = document.getElementById('x-quantity-' + sectionId);
          if (quantity) quantity.classList.add('unavailable');

          this._setBuyButtonUnavailable();
        },
        _setAvailable() {
          const selectors = ['block-inventory-', 'x-badges-', 'pickup-', 'sku-', 'back_in_stock_alert-'];
          for (let selector of selectors) {
            const element = document.getElementById(selector + sectionId);
            if (element) element.classList.remove('hidden');
          }
          const quantity = document.getElementById('x-quantity-' + sectionId);
          if (quantity) quantity.classList.remove('unavailable');
        },
        _setBuyButtonUnavailable() {
          const productForms = document.querySelectorAll(`#product-form-${sectionId},  #product-form-sticky-${sectionId}`);
          productForms.forEach((productForm) => {
            const addButton = productForm.querySelector('.add_to_cart_button');
            if (!addButton) return;
            addButton.setAttribute('disabled', 'disabled');
            const addButtonText = addButton.querySelector('.x-atc-text');
            if (addButtonText) addButtonText.textContent = unavailableText;
          });
        },
        initEventSticky() {
          document.addEventListener(`eurus:product-page-variant-select-sticky:updated:${sectionId}`, (e) => {
            this.handleSticky = e.detail.variantElSticky;
            this.updateVariantSelector(-1, e.detail.targetUrl);
          });
        },
        _getCurrentVariant(html) {
          this.currentVariant = JSON.parse(html.querySelector(`.variant-selects-main script[type="application/json"][data-selected-variant]`).textContent);
        },
        updateVariantSelector(inputId, target) {
          // Freeze the swatch pills' spread animation while the picker markup is
          // being re-fetched and swapped, so only the freshly-rendered selected
          // pill animates (once). Cleared in _updateOptionValues after the swap;
          // the timeout is a failsafe for render paths that don't rebuild the picker.
          element.classList.add('is-variant-swapping');
          clearTimeout(this._swatchSwapReset);
          this._swatchSwapReset = setTimeout(() => element.classList.remove('is-variant-swapping'), 1200);

          // Only this pill should replay the spread animation after the swap.
          // Guarded on a string: updateVariantSelector is also called with -1.
          this._animateInputId = (typeof inputId === 'string' && inputId) ? inputId : '';

          let updateFullpage = false;
          let callback = () => {};

          const targetUrl = target || element.dataset.url;
          if (element.dataset.url !== targetUrl) {
            if (isProductPage) {
              updateFullpage = true;
            }
            callback = (html) => {
              this._getCurrentVariant(html);
              this._setAvailable();
              this._updateURL(targetUrl);
              this._handleSwapProduct(sectionId, html, updateFullpage);
              this._handleSwapQuickAdd(html);
              this._renderCardBundle(html);
              this._renderCardFBT(html);
              this._dispatchUpdateVariant(html);
              this._dispatchVariantSelected(html);
            };
          } else {
            callback = (html) => {
              this._getCurrentVariant(html);
              this._setAvailable();
              this._updateURL(targetUrl);
              this._updateProductForms();
              this._handleUpdateProductInfo(html);
              this._updateOptionValues(html);
              this._updateMedia(html);
              this._handleAvailable(html);
              this._dispatchUpdateVariant(html);
              this._dispatchVariantSelected(html);
            }
          }
          this._renderProductInfo(targetUrl, callback, updateFullpage, inputId);
        },
        _renderProductInfo(url, callback, updateFullpage, inputId) {
          let link = "";
          const selectedOptionValues = this._getSelectedOptionValues();
          // Normally nothing renders until every option is chosen. In explicit-
          // selection mode the shopper starts from nothing and picks one at a time,
          // so render as soon as anything is selected: Shopify resolves the options
          // that are still unset to the first matching variant, which is exactly the
          // intent here (pick Orange for Flavor 1 -> Orange/Original).
          const minOptionValues = this.requireExplicitSelection ? 1 : optionNum;
          if (selectedOptionValues.length < minOptionValues) {
            // Nothing will load, so make sure no spinner is left running.
            this.loadingEls = '';
            return;
          }
          let params = `option_values=${selectedOptionValues.join(',')}`;
          link = updateFullpage?`${url}?${params}`:`${url}?section_id=${this.handleSectionId}&${params}`;
          Alpine.store('xHelper').cancelRequest('eurus:product_info_request');
          // Raise the spinner here - past the guard above, so it only ever appears
          // when a render is actually going to happen, and synchronously rather than
          // in requestAnimationFrame so it is up before the response can land.
          if (typeof inputId === 'string' && inputId) this.loadingEls = inputId;

          if (this.cachedResults[link]) {
            const html = this.cachedResults[link];
            callback(html);
            // The swap normally clears this, but not every callback path rebuilds the
            // picker - clear unconditionally so a cache hit can never strand it.
            this.loadingEls = '';
          } else if (this.prefetchInFlight[link]) {
            // Hovered, then clicked before the prefetch landed — ride that request
            // rather than opening a second identical one.
            this.prefetchInFlight[link]
              .then((html) => { if (html) callback(html); })
              .finally(() => { this.loadingEls = ''; });
          } else {
            const controller = new AbortController();
            Alpine.store('xHelper').requestControllers.set('eurus:product_info_request', controller);
            fetch(link, { signal: controller.signal })
              .then((response) => response.text())
              .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                callback(html);
                this.cachedResults[link] = html;
              })
              .catch(err => {
                if (err.name !== 'AbortError') {
                  console.error(err);
                }
              })
              .finally(() => {
                this.loadingEls = '';
              });
          }
          this.handleSticky = '';
        },
        _handleUpdateProductInfo(html) {
          this._renderCardBundle(html);
          this._renderCardFBT(html);
          this._renderPriceProduct(html);
          const selectors = ['product-variant-title-', 'x-block-product-score-', 'block-button-', 'product-description-block-', 'block-inventory-', 'block-available-quantity-', 'quantity-selector-', 'volume-', 'sku-', 'x-badges-', 'preorder-', 'cart-edt-'];
          for (let selector of selectors) {
            this._renderDestination(html, selector);
          }
          this._renderBuyButtons(html);
          this._setMessagePreOrder(html);
          this._setEstimateDelivery(html);
          
          const mtfSelectors = ['.properties_re_render', '.table_info_details', '.block-text', '.text-icon', '.collapsible-content', '.nutrition-bar-content', '.horizontab', '.featured-icon'];
          for (let selector of mtfSelectors) {
            this._setMetafieldInfo(html, selector);
          }
          this._setPickupPreOrder(html);
          this._setBackInStockAlert(html);
          Alpine.store('xUpdateVariantQuanity').render(html, this.handleSectionId);
          this._dispatchUpdateVariant(html);
          this._dispatchVariantSelected(html);
          this._updateOptionValues(html);
          Alpine.store('xPickupAvailable').updatePickUp(sectionId, this.currentVariant?.id);
        },
        initFirstAvailableVariant(el) {
          this.currentVariant = JSON.parse(el.querySelector(`script[type="application/json"][data-selected-variant]`).textContent);
          document.addEventListener('eurus:cart:items-changed', () => {
            this.cachedResults = [];
            Alpine.store('xUpdateVariantQuanity').updateQuantity(sectionId, productUrl, this.currentVariant?.id);
          });
        },
        _handleAvailable(html) {
          const selectedVariant = html.querySelector('.variant-selects-main [data-selected-variant]')?.innerHTML;
          if (selectedVariant == 'null') {
            this._setUnavailable();
          }
        },
        _updateOptionValues(html) {
          const variantSelects = html.querySelector('.variant-selects-main');
          if (variantSelects) {
            element.innerHTML = variantSelects.innerHTML;
            // Restore the checked state FIRST. In explicit-selection mode the server
            // markup carries no `checked` attributes at all, so until this loop runs
            // `:checked` matches nothing in the fresh markup.
            const inputs = element.querySelectorAll('input');
            Array.from(inputs).forEach(input => {
              if (input.dataset.valueSelected === 'true') input.checked = true;
              input.addEventListener('change', (e) => {
                this._markOptionTouched(e.target);
                setTimeout(() => {
                  this.updateVariantSelector(e.target.id, e.target.dataset.productUrl);
                }, 0);
              });
            });
            // Must run after the loop above re-checks everything the server resolved.
            this._clearUntouchedOptions();
            this._syncSelectionState();

            // The swap re-inserts EVERY pill, so the keyframes would replay on every
            // checked one - picking Flavor 2 re-animated the already-selected Flavor 1.
            // Suppress the spread on each checked pill except the one just clicked.
            // Done as an opt-OUT so the first paint of a page that has a variant
            // pre-selected still animates exactly as before.
            // MUST come after the checked state above and before the freeze is
            // released below, or there is nothing to match / nothing left to suppress.
            if (this._animateInputId) {
              element.querySelectorAll('.input-radio:checked + .swatch-pill').forEach((pill) => {
                const pillInput = pill.previousElementSibling;
                if (!pillInput || pillInput.id !== this._animateInputId) {
                  pill.classList.add('swatch-pill--no-animate');
                }
              });
            }

            // Fresh markup is in. Force a reflow so the browser registers the
            // pills in their frozen resting state, then release the freeze — the
            // animation-name flips from none to the keyframes and spreads once.
            void element.offsetWidth;
            element.classList.remove('is-variant-swapping');
            // Clear the loading flag now so the freshly-inserted selected pill
            // shows the spread animation, not the spinner that was covering the
            // outgoing pill during the fetch.
            this.loadingEls = '';
            Array.from(element.querySelectorAll('select')).forEach(select => {
              select.addEventListener('change', (e) => {
                this._markOptionTouched(e.target);
                setTimeout(() => {
                  Array.from(e.target.options).forEach(option => { option.removeAttribute('selected') });
                  e.target.options[e.target.selectedIndex].setAttribute('selected', 'selected');
                  this.updateVariantSelector(e.target.id, e.target.options[e.target.selectedIndex].dataset.productUrl);
                }, 0)
              });
            });
            // The picker just changed, so the set of one-click-away combinations
            // did too. Warm the new neighbours (hover prefetch still covers
            // desktop; this is what keeps taps instant on touch).
            if (isProductPage) this._warmAdjacentVariants();
          }
        },
        _getVariantData(inputId) {
          return JSON.parse(this._getVariantDataElement(inputId).textContent);
        },
        _getVariantDataElement(inputId) {
          return element.querySelector(`script[type="application/json"][data-resource="${inputId}"]`);
        },
        _updateURL(url) {
          if (!isProductPage) return;
          window.history.replaceState({}, '', `${url}${this.currentVariant?.id ? `?variant=${this.currentVariant?.id}` : ''}`);
        },
        _getSelectedOptionValues() {
          if (this.handleSticky == '') {
            return Array.from(element.querySelectorAll('select option[selected], fieldset input:checked')).map(
              (e) => e.dataset.optionValueId
            ).filter(id => id !== undefined);
          } else {
            return Array.from(this.handleSticky.querySelectorAll('select option[selected]')).map(
              (e) => e.dataset.optionValueId
            ).filter(id => id !== undefined);
          }
        },
        _renderCardBundle(html) {
          const destination = element.closest(".x-product-bundle-data");
          const card = html.getElementById('card-product-bundle-'+ this.handleSectionId);
          if (card) {
            const source = card.querySelector(".x-product-bundle-data");
            if (source && destination) destination.innerHTML = source.innerHTML;
          }
        },
        _renderCardFBT(html) {
          const destination = element.closest(".card-product-fbt");
          const source = html.querySelector('.card-product-fbt-clone .card-product-fbt');
          
          if (source && destination) destination.innerHTML = source.innerHTML;
        },
        _handleSwapProduct(sectionId, html, updateFullpage) {
          if (updateFullpage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            const destination = document.querySelector('main');
            const source = html.querySelector('main');
            if (source && destination) destination.innerHTML = source.innerHTML;
          } else {
            const destination = document.querySelector('.x-product-' + sectionId);
            const source = html.querySelector('.x-product-' + sectionId);
            if (source && destination) destination.innerHTML = source.innerHTML;
          }
        },
        _handleSwapQuickAdd(html) {
          const destination = element.closest(".choose-options-content");
          const source = html.querySelector('.choose-options-content');
          if (source && destination) destination.innerHTML = source.innerHTML;
        }
      }))
    });
  });

  
  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.store('xPickupAvailable', {
        cachedResults: [],
        updatePickUp(id, variantId) {
          const container = document.getElementsByClassName('pick-up-'+ id)[0];
          if (!container) return;

          const link = window.Shopify.routes.root + `variants/${variantId}/?section_id=pickup-availability`;
          Alpine.store('xHelper').cancelRequest('eurus:pickup_availability_request');
          if (this.cachedResults[link]) {
            container.innerHTML = this.cachedResults[link].innerHTML;
          } else {
            const controller = new AbortController();
            Alpine.store('xHelper').requestControllers.set('eurus:pickup_availability_request', controller);
            fetch(link, { signal: controller.signal })
            .then(response => response.text())
            .then(text => {
              const pickupAvailabilityHTML = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector('.shopify-section');  
              this.cachedResults[link] = pickupAvailabilityHTML;
              container.innerHTML = pickupAvailabilityHTML.innerHTML;
            })
            .catch(e => {
              console.error(e);
            }); 
          }
        }
      });
    });
  });
  
  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.store('xUpdateVariantQuanity', {
        cachedResults: [],
        updateQuantity(sectionId, productUrl, currentVariant) {
          const quantity = document.getElementById('x-quantity-' + sectionId);
          const pricingPB = document.getElementById('x-pricing-progress-bar-' + sectionId);
          if (!quantity && !pricingPB) return;
          const url = currentVariant ? `${productUrl}?variant=${currentVariant}&section_id=${sectionId}` : `${productUrl}?section_id=${sectionId}`;
          Alpine.store('xHelper').cancelRequest('eurus:variant_quantity_request');
          if (this.cachedResults[url]) {
            this.render(this.cachedResults[url], sectionId);
          } else {
            const controller = new AbortController();
            Alpine.store('xHelper').requestControllers.set('eurus:variant_quantity_request', controller);
            fetch(url, { signal: controller.signal })
              .then((response) => response.text())
              .then((responseText) => {
                let html = new DOMParser().parseFromString(responseText, 'text/html');
                this.cachedResults[url] = html;
                this.render(html, sectionId);
              });
          }
        },
        render(html, sectionId) {
          const destination = document.getElementById('x-quantity-' + sectionId);
          const source = html.getElementById('x-quantity-'+ sectionId);
          if (source && destination) destination.innerHTML = source.innerHTML;

          const destinationPricingPB = document.getElementById('x-pricing-progress-bar-' + sectionId);
          const sourcePricingPB = html.getElementById('x-pricing-progress-bar-' + sectionId);
          if (sourcePricingPB && destinationPricingPB) destinationPricingPB.innerHTML = sourcePricingPB.innerHTML;     
        }
      });
    });
  });
}